import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TopNav from "../common/topnav";
import SideNave from "../common/sidenav";
import Loading from "../loading/loading";
import "../common/style.css";
import { employeeFormConfig, employeeFormTabs } from "../../form/FormData";
import DynamicForm from "../../form/DynamicForm";
import {
  createEmployee,
  downloadFile,
  getDepartmentList,
  getDesignationList,
  getEmployeeById,
  getStateList,
  updateEmployee,
  uploadFile,
} from "../http/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditEmployee() {
  const navigate = useNavigate();
  const location = useLocation();
  const employee = location.state?.employee || null;

  const [loading, setLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [states, setStates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [files, setFiles] = useState({});
  const [formData, setFormData] = useState({
    id: null,
    photo: null,
    name: "",
    dateOfBirth: "",
    gender: "",
    joiningDate: "",
    bloodGroup: "",
    resignationDate: "",
    contractDate: "",
    designation: "",
    department: "",
    reportingManager: "",
    personalMobileNo: "",
    officeMobileNo: "",
    personalEmail: "",
    officeEmail: "",
    permanentAddress: "",
    permanentCity: "",
    permanentState: "",
    permanentPinCode: "",
    mailingAddress: "",
    mailingCity: "",
    mailingState: "",
    mailingPinCode: "",
    panNumber: "",
    panCardDocument: null,
    aadhaarNumber: "",
    aadhaarCardDocument: null,
    cvDocument: null,
    contractDocument: null,
    lastQualification: "",
    lastQualificationCertificateDocument: null,
    passportDocument: null,
    drivingLicenseDocument: null,
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    roles: "",
    pfUan: "",
    esiNumber: "",
    password: "",
    branchName: "",
    emergencyContacts: [{ name: "", relationship: "", mobileNumber: "" }],
    salary: [
      {
        basicSalary: 0,
        hra: 0,
        da: 0,
        tdsDeduction: 0,
        professionalTax: 0,
        otherAllowance: 0,
        pfEmployeeContribution: 0,
        pfEmployerContribution: 0,
        esiContribution: 0,
        effectiveDate: "",
        payRoleCompany: "",
      },
    ],
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getStateList().then((res) => setStates(res.data));
    getDepartmentList().then((res) => setDepartments(res.data));
    getDesignationList().then((res) => setDesignations(res.data));
  }, []);

  useEffect(() => {
    if (employee?.id) {
      setLoading(true);
      getEmployeeById(employee.id)
        .then((res) => {
          const empData = res.data;

          console.log("Employee JSON Data:", JSON.stringify(empData, null, 2));

          // Update formData
          setFormData((prev) => ({ ...prev, ...empData }));

          // Prepare files object for previews/downloads
          const fileFields = [
            "photo",
            "panCardDocument",
            "aadhaarCardDocument",
            "cvDocument",
            "contractDocument",
            "lastQualificationCertificateDocument",
            "passportDocument",
            "drivingLicenseDocument",
          ];

          const existingFiles = {};
          fileFields.forEach((field) => {
            if (empData[field]) {
              const isImage = empData[field].match(/\.(jpeg|jpg|png|gif)$/i);
              existingFiles[field] = {
                file: null,
                previewUrl: isImage ? `/uploads/${empData[field]}` : null,
                name: empData[field],
              };
            }
          });
          setFiles(existingFiles);
        })
        .catch((err) => {
          console.error("Failed to fetch employee:", err);
          toast.error("Failed to load employee data.");
        })
        .finally(() => setLoading(false));
    }
  }, [employee]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (currentTabId = null) => {
    const newErrors = { ...errors }; // Start with existing errors

    // Filter sections to validate only current tab if specified
    const sectionsToValidate = currentTabId
      ? employeeFormConfig.filter((section) => section.id === currentTabId)
      : employeeFormConfig;

    sectionsToValidate.forEach((section) => {
      section.fields.forEach((field) => {
        const value = formData[field.name];

        // Clear existing error for this field if we're validating current tab
        if (currentTabId) {
          delete newErrors[field.name];
        }

        if (field.required && !value) {
          newErrors[field.name] =
            field.errorMessage || "This field is required";
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Validate all tabs
      toast.error("Please fix all validation errors before submitting.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      console.log("Employee Payload:", JSON.stringify(payload, null, 2));
      if (formData.id) {
        await updateEmployee(payload);
        toast.success("Employee updated successfully!");
      } else {
        const response = await createEmployee(payload);
        payload.id = response.data.id;
        toast.success("Employee added successfully!");
      }
      setTimeout(() => navigate("/employee"), 1500);
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(
        error.response?.data?.message ||
          "Unexpected error while saving the employee."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/employee");
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleFileUpload = async (fieldName, file) => {
    try {
      const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, "");
      const uniqueFileName = `${timestamp}_${file.name}`;
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });

      const formDataObj = new FormData();
      formDataObj.append("file", uniqueFile);
      formDataObj.append("keyName", uniqueFile.name);

      const res = await uploadFile(formDataObj);
      const savedFileName = res.data?.fileName || uniqueFile.name;

      setFormData((prev) => ({ ...prev, [fieldName]: savedFileName }));

      setFiles((prev) => ({
        ...prev,
        [fieldName]: {
          file: uniqueFile,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(uniqueFile)
            : null,
        },
      }));

      toast.success(`${uniqueFile.name} uploaded successfully`);
    } catch (err) {
      console.error("File upload failed:", err);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const handleFileDownload = async (fileName) => {
    try {
      const fileBlob = await downloadFile(fileName);
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(new Blob([fileBlob.data]));
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Failed to download file.");
    }
  };

  return (
    <div className="ShowEmployee">
      {loading && <Loading />}
      <ToastContainer position="top-right" autoClose={3000} />
      <div className={`sb-nav-fixed ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <TopNav toggleSidebar={toggleSidebar} />
        <div id="layoutSidenav">
          <SideNave
            isSidebarCollapsed={isSidebarCollapsed}
            toggleSidebar={toggleSidebar}
          />
          <div id="layoutSidenav_content">
            <main>
              <DynamicForm
                key={formData.id || "new"} // ✅ force remount on data change
                formName="Employee"
                formConfig={employeeFormConfig}
                formData={formData}
                onChange={handleChange}
                onSubmit={handleSave}
                onBack={handleBack}
                isEditing={!!formData.id}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                states={states}
                departments={departments}
                designations={designations}
                tabs={employeeFormTabs}
                errors={errors}
                files={files}
                setFiles={setFiles}
                onUpload={handleFileUpload}
                onDownload={handleFileDownload}
                validateTab={validateForm} 
              />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
